import Route from '@ember/routing/route';
import { hash } from 'rsvp';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { routeEvent } from 'garaje/utils/decorators/route';

export default class MapsEditShowRoute extends Route {
  @service store;
  @service state;
  @service metrics;
  @service transitionConfirm;
  @service router;
  @service abilities;
  @service featureFlags;
  @service featureConfig;

  queryParams = {
    floor_id: { refreshModel: true },
  };

  beforeModel() {
    if (this.abilities.cannot('visit edit maps')) {
      this.router.transitionTo('spaces.maps.live');
    } else {
      this.metrics.trackEvent('Accessed edit map');
    }
  }

  async model(_params, transition) {
    const { currentLocation } = this.state;
    const { areaMap } = this.modelFor('spaces.maps');
    const floor_id = transition?.to?.queryParams?.floor_id;

    if (!floor_id) {
      const firstMapFloor = (await areaMap.mapFloors).sortBy('ordinality').slice()[0];
      if (firstMapFloor) {
        this.router.replaceWith('spaces.maps.edit.show', {
          queryParams: { floor_id: firstMapFloor.id },
        });
        return;
      }
    }
    if (this.featureFlags.isEnabled('react-producer-map') && this.featureConfig.isEnabled('desks.unlimitedDesks')) {
      return hash({
        areaMap,
        mapFloor: this.store.findRecord('map-floor', floor_id),
      });
    }
    const desksInLocation = this.store.query('desk', {
      filter: {
        'location-id': currentLocation.id,
        booked: true,
        permanentlyAssigned: true,
      },
      include: 'assignments',
    });

    return hash({
      areaMap,
      desksInLocation,
      mapFloor: this.store.findRecord('map-floor', floor_id),
      mapFeatures: this.store.query('map-feature', {
        filter: {
          'area-map': areaMap.id,
        },
      }),
      genericResources: this.abilities.can('use generic resource')
        ? this.store.query('generic-resource', {
            'location-id': currentLocation.id,
          })
        : [],
    });
  }

  async afterModel({ mapFloor }) {
    const { areaMap } = this.modelFor('spaces.maps');
    const mapFloors = await areaMap.mapFloors.content;
    const hasFloorInLocation = mapFloors.any((floor) => floor.id == mapFloor.id);

    // added specifically for when you change locations
    if (!hasFloorInLocation) {
      this.router.transitionTo('spaces.maps.edit.show', {
        queryParams: { floor_id: mapFloors[0].id },
      });
    }
  }

  setupController(controller, { mapFeatures, genericResources }) {
    super.setupController(...arguments);

    if (this.featureFlags.isEnabled('react-producer-map') && this.featureConfig.isEnabled('desks.unlimitedDesks')) {
      return;
    }
    controller.mapFeatures = mapFeatures.slice();
    controller.genericResources = genericResources.slice();
    controller.loadResoucesDataTask.perform();
  }

  resetController(controller, isExiting, transition) {
    if (isExiting && transition.targetName !== 'error') {
      controller.resetData();
    }
  }

  @routeEvent
  routeWillChange(transition) {
    const controller = this.controller;
    const dirtyFeatures = controller.mapFeatures.filterBy('hasDirtyAttributes');
    const deletableFeatures = controller.deletableFeatures;
    const desksWithDirtyAttributes = controller.desksWithDirtyAttributes;
    const dirtyNeighborhoods = controller.dirtyNeighborhoods;

    if (
      isEmpty(dirtyFeatures) &&
      isEmpty(deletableFeatures) &&
      isEmpty(desksWithDirtyAttributes) &&
      isEmpty(dirtyNeighborhoods) &&
      !controller.hasDirtyAssignments()
    ) {
      return;
    }

    this.transitionConfirm.displayConfirmTask.perform(transition, {
      continue() {
        // Only rollback records that are still valid and not destroyed
        dirtyFeatures.forEach((feature) => {
          if (!feature.isDestroyed && !feature.isDestroying) {
            feature.rollbackAttributes();
          }
        });

        deletableFeatures.forEach((feature) => {
          if (!feature.isDestroyed && !feature.isDestroying) {
            feature.rollbackAttributes();
          }
        });

        desksWithDirtyAttributes.forEach((desk) => {
          if (!desk.isDestroyed && !desk.isDestroying) {
            desk.rollbackAttributes();
          }
        });

        dirtyNeighborhoods.forEach((neighborhood) => {
          if (!neighborhood.isDestroyed && !neighborhood.isDestroying) {
            neighborhood.rollbackAttributes();
          }
        });

        controller.clearDirtyAssignments();
        controller.selectedFeature = null;

        // Only add back features that are still valid
        const validDirtyFeatures = dirtyFeatures.filter((feature) => !feature.isDestroyed && !feature.isDestroying);
        controller.mapFeatures = [...controller.mapFeatures, ...validDirtyFeatures];
        controller.deletableFeatures = [];
      },
    });
  }
}
